import React, { useState, useEffect } from 'react';
import { API_PATHS } from '../../../utils/constants';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  CircularProgress,
  Typography,
  Box,
  Alert,
  Divider
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import axios from 'axios';
import api from '../../../services/api';
import authService from '../../../services/auth';
import AppService from '../../../services/AppService';
import { v4 as uuidv4, parse as uuidParse } from 'uuid';
import ServiceMappingDialog from './ServiceMappingDialog';

/**
 * QuickAssignDialog component for quickly assigning tickets to users
 * 
 * @param {Object} props Component props
 * @param {boolean} props.open Whether the dialog is open
 * @param {Function} props.onClose Function to call when the dialog is closed
 * @param {string} props.ticketId The ID of the ticket to assign
 * @param {Object} props.ticket The ticket object with details
 * @param {Function} props.onAssignmentComplete Callback function called when assignment is complete
 */
const QuickAssignDialog = ({ open, onClose, ticketId, ticket, onAssignmentComplete }) => {
  // State for users
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [notes, setNotes] = useState('');
  const [dueDate, setDueDate] = useState(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)); // Default: 1 week from now
  
  // State for loading and errors
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Get current user's organization ID
  const [organizationId, setOrganizationId] = useState(null);

  // State for service mapping dialog
  const [showMappingDialog, setShowMappingDialog] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [customerGovtCharge, setCustomerGovtCharge] = useState(0);

  // State for mapping status indicator
  const [mappingStatus, setMappingStatus] = useState(null); // null | 'checking' | 'mapped' | 'not_mapped'
  const [mappingCheckError, setMappingCheckError] = useState(null);
  const [partnerServiceMapping, setPartnerServiceMapping] = useState(null);

  const [showAssignConfirmDialog, setShowAssignConfirmDialog] = useState(false);
  const [assignConfirmMessage, setAssignConfirmMessage] = useState('');
  const [pendingAssign, setPendingAssign] = useState(false);

  // Fetch current user and organization info when dialog opens
  useEffect(() => {
    if (open) {
      fetchCurrentUserInfo();
    }
  }, [open]);

  // Fetch current user info and users from the same organization
  const fetchCurrentUserInfo = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get the token with detailed logging
      let token = localStorage.getItem('jwt_token');
      console.log('Initial token check from jwt_token:', token ? 'Found token' : 'No token found');
      
      // Try fallback token sources if the primary source fails
      if (!token) {
        // Try the access_token key which might be used in some auth implementations
        token = localStorage.getItem('access_token');
        console.log('Fallback token check from access_token:', token ? 'Found token' : 'No token found');
        
        // Try getting token from authInfo structure
        if (!token) {
          try {
            const authInfo = localStorage.getItem('authInfo');
            if (authInfo) {
              const authInfoObj = JSON.parse(authInfo);
              token = authInfoObj.token || authInfoObj.accessToken || authInfoObj.jwt_token;
              console.log('Fallback token check from authInfo:', token ? 'Found token' : 'No token found');
            }
          } catch (authParseError) {
            console.error('Error parsing authInfo for token:', authParseError);
          }
        }
      }
      
      // Final check if we have a token from any source
      if (!token) {
        console.error('No authentication token available from ticket assign');
        setError('Authentication error. Please log in again.');
        setLoading(false);
        return;
      }
      
      console.log('Fetching users for ticket assignment');
      
      // Setup headers with token
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      
      const API_URL = API_PATHS.BASE_URL;
      
      // Determine the current user type and organization type
      let userType = 'UNKNOWN';
      let orgType = 'PARTNER_ORG'; // Default to partner organization
      
      // Get current user info from localStorage
      const authInfo = localStorage.getItem('authInfo');
      if (authInfo) {
        try {
          const parsedAuth = JSON.parse(authInfo);
          userType = parsedAuth?.principalInfo?.userType || 
                      parsedAuth?.user?.userType || 
                      parsedAuth?.userType || 'UNKNOWN';
          
          // Map the user type to the corresponding organization type
          if (userType === 'ADMIN_ORG' || userType === 'ADMIN') {
            userType = 'ADMIN';
            orgType = 'PARTNER_ORG'; // Admins work with partner orgs
          } else if (userType === 'CUSTOMER_ORG' || userType === 'CUSTOMER') {
            userType = 'CUSTOMER';
            orgType = 'CUSTOMER_ORG';
          } else if (userType === 'PARTNER_ORG' || userType === 'PARTNER') {
            userType = 'PARTNER';
            orgType = 'PARTNER_ORG';
          }
          
          console.log(`Current user type: ${userType}, using organization type: ${orgType}`);
        } catch (parseError) {
          console.error('Error parsing auth info:', parseError);
        }
      } else {
        console.log('No auth info found, defaulting to PARTNER_ORG');
      }
      
      // Different approach based on user type
      if (userType === 'ADMIN') {
        // ADMIN users should fetch both partner organizations and the customer org that created the ticket
        try {
          console.log('Admin user detected, fetching partner organizations...');
          // Create an array to hold all promises
          const promises = [];
          
          // First get all partner organizations - use AppService to ensure consistent URL handling
          console.log('Making API call to get partners:', `${API_PATHS.API_URL}/v1/user/byType?type=PARTNER_ORG`);
          promises.push(AppService.get(`/v1/user/byType?type=PARTNER_ORG`));
          
          // Always get current user details
          console.log('Making API call to get current user:', `${API_PATHS.API_URL}/v1/user/current`);
          promises.push(AppService.get(`/v1/user/current`));
          
          let usersToDisplay = [];
          
          try {
            // Wait for both API calls to complete
            const [partnerResponse, currentUserResponse] = await Promise.all(promises);
            
            // Initialize empty array for users
            usersToDisplay = [];
            
            // Process partner organization users
            console.log('Partner response raw:', partnerResponse);
            let partnerData = [];
            
            // Handle different response formats for partner data
            if (partnerResponse?.data && Array.isArray(partnerResponse.data)) {
              partnerData = partnerResponse.data;
            } else if (Array.isArray(partnerResponse)) {
              partnerData = partnerResponse;
            } else if (partnerResponse && partnerResponse.content && Array.isArray(partnerResponse.content)) {
              partnerData = partnerResponse.content;
            }
            
            if (partnerData.length > 0) {
              console.log('Partner users found:', partnerData.length);
              console.log('Partner user details:', JSON.stringify(partnerData));
              // Add partner users to the list and mark them as partners
              usersToDisplay = partnerData.map(user => ({ ...user, _isPartner: true }));
            } else {
              console.warn('No partner data found or data is not in expected format');
            }
            
            // Process current user data
            console.log('Current user response raw:', currentUserResponse);
            let currentUserData = null;
            
            // Handle different response formats for current user
            if (currentUserResponse?.data) {
              currentUserData = currentUserResponse.data;
            } else if (typeof currentUserResponse === 'object' && currentUserResponse !== null) {
              currentUserData = currentUserResponse;
            }
            
            if (currentUserData) {
              console.log('Current user details:', JSON.stringify(currentUserData));
              // Check if current user is already in the list to avoid duplicates
              const currentUserExists = usersToDisplay.some(user => user.id === currentUserData.id);
              if (!currentUserExists) {
                usersToDisplay.push(currentUserData);
                console.log('Added current user to the list');
              } else {
                console.log('Current user already exists in the list, not adding duplicate');
              }
            } else {
              console.warn('No current user data found or data is not in expected format');
            }
            
            // Log final combined users list
            console.log('Final combined users list:', usersToDisplay.length, 'users');
            usersToDisplay.forEach((user, index) => {
              console.log(`User ${index + 1}:`, user.name || user.username || user.email || 'Unknown user');
            });
            
            // If we have a ticket, also get the customer org that created it
            if (ticket && ticket.createdOrganisation && ticket.createdOrganisation.id) {
              console.log('Fetching users from ticket creator organization...');
              const customerOrgId = ticket.createdOrganisation.id;
              
              try {
                // Get users from this specific customer organization
                const customerResponse = await AppService.get(
                  `/v1/organisation/${customerOrgId}/users`
                );
                
                if (customerResponse?.data && Array.isArray(customerResponse.data)) {
                  console.log(`Found ${customerResponse.data.length} users from ticket creator organization`);
                  // Add only users that aren't already in the list
                  const newUsers = customerResponse.data.filter(cusUser => 
                    !usersToDisplay.some(existingUser => existingUser.id === cusUser.id)
                  );
                  usersToDisplay = [...usersToDisplay, ...newUsers];
                }
              } catch (custError) {
                console.warn('Error fetching customer organization users:', custError);
              }
            }
          } catch (error) {
            console.error('Error fetching users:', error);
          }
          
          if (usersToDisplay.length > 0) {
            setUsers(usersToDisplay);
            setLoading(false);
            return;
          } else {
            console.log('No users found for assignment, will try fallback methods');
          }
        } catch (adminError) {
          console.warn('Error fetching users for admin:', adminError);
          // Continue with fallback methods
        }
      } else {
        // Non-admin users should get users from their current organization
        try {
          console.log(`Fetching users by type in current organization (${orgType})...`);
          const response = await AppService.get(`/v1/user/byTypeInCurrentOrg?type=${orgType}`);
          
          if (response?.data && Array.isArray(response.data) && response.data.length > 0) {
            console.log(`Found ${response.data.length} users from current organization`);
            setUsers(response.data);
            
            // Set organization ID from the first user if available
            if (response.data[0]?.organisation?.id) {
              setOrganizationId(response.data[0].organisation.id);
            }
            
            // If successfully retrieved users, we're done
            setLoading(false);
            return;
          } else {
            console.log('No users found in the current organization, will try fallback methods');
          }
        } catch (apiError) {
          console.warn('Error fetching users by type in current organization:', apiError);
          // Continue with fallback methods
        }
      }
      
      // Fallback - Try to get users by type (existing endpoint)
      try {
        // For the fallback, we'll use PARTNER_ORG when we're dealing with an admin
        // This ensures we always get partner users as a fallback for admins
        const fallbackOrgType = userType === 'ADMIN' ? 'PARTNER_ORG' : 'CUSTOMER_ORG';
        console.log(`Using fallback organization type: ${fallbackOrgType}`);
        
        // Use the /v1/user/byType endpoint 
        const userTypeResponse = await AppService.get(`/v1/user/byType?type=${fallbackOrgType}`);
        console.log('User type response:', userTypeResponse);
        
        if (userTypeResponse?.data && Array.isArray(userTypeResponse.data) && userTypeResponse.data.length > 0) {
          console.log('Found customer organization users:', userTypeResponse.data);
          setUsers(userTypeResponse.data);
          
          // Set organization ID if available
          const firstUser = userTypeResponse.data[0];
          if (firstUser && firstUser.organisation && firstUser.organisation.id) {
            setOrganizationId(firstUser.organisation.id);
          }
          setLoading(false);
          return;
        }
      } catch (typeError) {
        console.warn('Error fetching users by type:', typeError);
        // Continue to next fallback
      }
      
      // Step 3: Try to get current user's details as a fallback
      try {
        console.log('Attempting to get current user details');
        const currentUserResponse = await AppService.get(`/v1/user/current`);
        
        if (currentUserResponse?.data) {
          console.log('Retrieved current user details:', currentUserResponse.data);
          setUsers([currentUserResponse.data]);
          
          if (currentUserResponse.data.organisation?.id) {
            setOrganizationId(currentUserResponse.data.organisation.id);
          }
          setLoading(false);
          return;
        }
      } catch (currentUserError) {
        console.warn('Failed to get current user details:', currentUserError);
      }
      
      // Step 4: Try getting user details from localStorage
      // Get current user from localStorage
      const currentUserId = localStorage.getItem('user_id');
      const currentUserName = localStorage.getItem('user_name');
      const userEmail = localStorage.getItem('user_email');
      const orgId = localStorage.getItem('organization_id');
      const organizationName = localStorage.getItem('organization_name');
      
      if (currentUserId && currentUserName) {
        console.log('Using current user from localStorage as fallback');
        // Create a real user object from localStorage values
        const currentUser = {
          id: currentUserId,
          name: currentUserName,
          email: userEmail || '',
          organisation: { 
            id: orgId || 'unknown-org', 
            name: organizationName || 'Your Organization' 
          }
        };
        
        // Set this as our only user
        setUsers([currentUser]);
        
        if (orgId) {
          setOrganizationId(orgId);
        }
        setLoading(false);
        return;
      }
      
      // Step 5: If all else fails, try to get info from authInfo
      try {
        const authInfo = localStorage.getItem('authInfo');
        if (authInfo) {
          const authInfoObj = JSON.parse(authInfo);
          if (authInfoObj.principalInfo) {
            const currentUser = {
              id: authInfoObj.principalInfo.id || '00000000-0000-0000-0000-000000000001',
              name: authInfoObj.principalInfo.name || 'Current User',
              email: authInfoObj.principalInfo.email || 'current.user@example.com'
            };
            
            console.log('Using current user from authInfo as final fallback:', currentUser);
            setUsers([currentUser]);
            setLoading(false);
            return;
          }
        }
      } catch (authInfoError) {
        console.error('Failed to parse authInfo:', authInfoError);
      }
      
      // If we got here, we couldn't get any real users
      setError('Failed to load user information. Please try again.');
    } catch (error) {
      console.error('Error fetching users from organization:', error);
      setError('Failed to load user information. Please try again.');
    } finally {
      setLoading(false);
    }
  };



  // Handle user selection
  const handleUserChange = async (event) => {
    const userId = event.target.value;
    setSelectedUser(userId);

    // Reset mapping status
    setMappingStatus(null);
    setMappingCheckError(null);
    setPartnerServiceMapping(null);

    // Check if selected user is a partner and ticket has service
    const selectedUserObj = users.find(u => u.id === userId);
    console.log('[DEBUG] Selected user object:', selectedUserObj);

    const isPartner = selectedUserObj?._isPartner === true ||
                      selectedUserObj?.organisation?.type === 'PARTNER' ||
                      selectedUserObj?.userType === 'PARTNER_ORG';
    console.log('[DEBUG] isPartner:', isPartner);
    console.log('[DEBUG] _isPartner flag:', selectedUserObj?._isPartner);
    console.log('[DEBUG] organisation.type:', selectedUserObj?.organisation?.type);
    console.log('[DEBUG] userType:', selectedUserObj?.userType);

    // Get service ID from ticket.service.id or fall back to ticket.serviceId
    const serviceId = ticket?.service?.id || ticket?.serviceId;
    console.log('[DEBUG] serviceId:', serviceId);
    console.log('[DEBUG] ticket.service:', ticket?.service);
    console.log('[DEBUG] ticket.serviceId:', ticket?.serviceId);

    if (isPartner && serviceId) {
      // Check service mapping
      setMappingStatus('checking');

      try {
        // Try to get organisation ID from multiple possible locations
        let partnerId = selectedUserObj.organisation?.id ||
                       selectedUserObj.organisationId ||
                       selectedUserObj.organization?.id;

        console.log('[DEBUG] Initial partnerId check:', partnerId);
        console.log('[DEBUG] selectedUserObj.organisation:', selectedUserObj.organisation);
        console.log('[DEBUG] selectedUserObj.organisationId:', selectedUserObj.organisationId);

        if (!partnerId) {
          console.log('Organisation ID not found in user object, fetching user details...');
          try {
            const userDetailsResponse = await AppService.get(`/v1/user/details?userId=${userId}`);
            const userDetails = userDetailsResponse?.data || userDetailsResponse;

            // Try multiple field names for organisation ID
            partnerId = userDetails?.organisation?.id ||
                       userDetails?.organisationId ||
                       userDetails?.organization?.id ||
                       userDetails?.orgId;

            console.log('[DEBUG] Fetched user details:', userDetails);
            console.log('[DEBUG] Extracted organisation ID:', partnerId);

            // Update the selected user object with organisation info for later use
            if (partnerId) {
              selectedUserObj.organisation = {
                id: partnerId,
                name: userDetails?.organisation?.name ||
                     userDetails?.organisationName ||
                     userDetails?.organization?.name ||
                     selectedUserObj.organisationName
              };
            }
          } catch (fetchError) {
            console.error('Error fetching user details:', fetchError);
          }
        }

        // If we still don't have organisation ID, we can't proceed with mapping check
        if (!partnerId) {
          console.error('Unable to determine partner organisation ID');
          console.error('Full user object:', JSON.stringify(selectedUserObj, null, 2));
          setMappingCheckError('Unable to determine partner organization. Please contact support.');
          setMappingStatus(null);
          return;
        }

        console.log('Checking service mapping for partner:', partnerId, 'service:', serviceId);

        const mappingResponse = await checkServiceMapping(partnerId, serviceId);

        if (mappingResponse && (mappingResponse.isExisting || mappingResponse.existing)) {
          setMappingStatus('mapped');
          setPartnerServiceMapping(mappingResponse);
          console.log('✓ Service is already mapped to this partner');
        } else {
          setMappingStatus('not_mapped');
          setPartnerServiceMapping(null);
          setSelectedPartner(selectedUserObj);
          setCustomerGovtCharge(mappingResponse?.govtCharge || ticket.service.govtCharge || 0);
          console.log('⚠ Service is NOT mapped to this partner');
        }
      } catch (error) {
        console.error('Error checking service mapping:', error);
        setMappingCheckError('Unable to check service mapping');
        setMappingStatus(null);
        setPartnerServiceMapping(null);
      }
    } else if (isPartner && !ticket?.service?.id) {
      console.warn('Cannot check service mapping: ticket.service.id is missing');
      setMappingCheckError('Service information not available');
    }
  };

  // Handle notes change
  const handleNotesChange = (event) => {
    setNotes(event.target.value);
  };

  // Handle due date change
  const handleDueDateChange = (newDate) => {
    setDueDate(newDate);
  };

  // Handle when service mapping is created successfully
  const handleMappingCreated = async () => {
    console.log('Service mapping created successfully');
    setShowMappingDialog(false);
    setError(null);

    const serviceId = ticket?.service?.id || ticket?.serviceId;
    const partnerId =
      selectedPartner?.organisation?.id ||
      selectedPartner?.organisationId ||
      selectedPartner?.organization?.id ||
      selectedPartner?.id;

    if (!serviceId || !partnerId) {
      setMappingStatus('mapped');
      setPartnerServiceMapping(null);
      setSuccess('Service mapping created successfully! You can now assign the ticket.');
      return;
    }

    try {
      setMappingStatus('checking');
      const mappingResponse = await checkServiceMapping(partnerId, serviceId);
      if (mappingResponse && (mappingResponse.isExisting || mappingResponse.existing)) {
        setMappingStatus('mapped');
        setPartnerServiceMapping(mappingResponse);
      } else {
        setMappingStatus('mapped');
        setPartnerServiceMapping(mappingResponse || null);
      }
    } catch (e) {
      console.error('Error refreshing mapping after create:', e);
      setMappingStatus('mapped');
      setPartnerServiceMapping(null);
    }

    setSuccess('Service mapping created successfully! You can now assign the ticket.');
  };

  // Check service-partner mapping before assignment
  const checkServiceMapping = async (partnerId, serviceId) => {
    try {
      console.log('Checking service mapping for partner:', partnerId, 'service:', serviceId);

      const response = await AppService.get(
        `/v1/serviceMapping?organisationId=${partnerId}&serviceId=${serviceId}`
      );

      console.log('Service mapping check response:', response);

      // Response contains isExisting flag
      return response?.data || response;
    } catch (error) {
      console.error('Error checking service mapping:', error);
      throw error;
    }
  };

  const getNumber = (value) => {
    const n = typeof value === 'string' ? parseFloat(value) : Number(value);
    return Number.isFinite(n) ? n : 0;
  };

  const isOrganizationTicket = () => {
    const assignedToVal = ticket?.assignment?.assignedTo || ticket?.assignedTo;
    const assignedToIsOrg = String(assignedToVal || '').toUpperCase() === 'ORGANIZATION';
    const totalCost = getNumber(ticket?.totalCost ?? ticket?.ticket?.totalCost);
    return assignedToIsOrg || totalCost === 0;
  };

  const getTicketChargeIncTax = () => {
    const serviceCharge = getNumber(ticket?.serviceCharge ?? ticket?.ticket?.serviceCharge);
    const tax = getNumber(
      ticket?.tax ??
      ticket?.ticket?.tax ??
      ticket?.taxAmount ??
      ticket?.ticket?.taxAmount ??
      ticket?.taxOnServiceCharge ??
      ticket?.ticket?.taxOnServiceCharge
    );
    return serviceCharge + tax;
  };

  const getPartnerChargeIncTax = () => {
    const serviceCharge = getNumber(partnerServiceMapping?.serviceCharge);
    const tax = getNumber(partnerServiceMapping?.taxOnServiceCharge);
    return serviceCharge + tax;
  };

  const performAssignApiCall = async () => {
    try {
      setSubmitting(true);
      setError(null);
      setSuccess(null);

      console.log('Starting assignment process...');
      
      // Get the token with detailed logging
      let token = localStorage.getItem('jwt_token');
      console.log('Initial token check from jwt_token for assignment:', token ? 'Found token' : 'No token found');
      
      // Try fallback token sources if the primary source fails
      if (!token) {
        // Try the access_token key which might be used in some auth implementations
        token = localStorage.getItem('access_token');
        console.log('Fallback token check from access_token for assignment:', token ? 'Found token' : 'No token found');
        
        // Try getting token from authInfo structure
        if (!token) {
          try {
            const authInfo = localStorage.getItem('authInfo');
            if (authInfo) {
              const authInfoObj = JSON.parse(authInfo);
              token = authInfoObj.token || authInfoObj.accessToken || authInfoObj.jwt_token;
              console.log('Fallback token check from authInfo for assignment:', token ? 'Found token' : 'No token found');
            }
          } catch (authParseError) {
            console.error('Error parsing authInfo for token during assignment:', authParseError);
          }
        }
      }
      
      // Final check if we have a token from any source
      if (!token) {
        console.error('No token found in localStorage for ticket assignment');
        setError('Authentication token not found. Please log in again.');
        setSubmitting(false);
        return;
      }
      
      console.log('Token retrieved successfully');
      
      // Setup headers with token
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      
      // Log the user selection
      console.log('Selected user ID:', selectedUser);
      
      // We now have a dedicated endpoint for assigning tickets to users
      console.log('Attempting to assign ticket via assignToUser endpoint');
      
      // Prepare assignment data with all relevant details
      const assignmentData = {
        ticketId: ticketId,
        userId: selectedUser,
        notes: notes,
        dueDate: dueDate ? dueDate.toISOString() : null
      };
      
      console.log('Assignment data prepared:', assignmentData);
      
      // Call the new assignToUser endpoint to assign the ticket to the selected user
      console.log('Making API request to endpoint:', '/v1/ticket/assignToUser');
      
      // Use AppService which properly handles URL formatting and authentication
      const response = await AppService.post('/v1/ticket/assignToUser', assignmentData);
      
      // Handle different response formats safely
      console.log('Assignment API response:', response); // Log full response for debugging
      
      // No need to access the data property directly, which might not exist
      // Just check if the response exists which indicates success
      if (response) {
        // Show success message
        const successMessage = 'Ticket successfully assigned to user.';
        console.log(successMessage);
        setSuccess(successMessage);
      } else {
        throw new Error('Empty response received from server');
      }
      
      // Reset form
      setNotes('');
      
      // Call onAssignmentComplete callback if provided
      if (onAssignmentComplete && typeof onAssignmentComplete === 'function') {
        onAssignmentComplete();
      }
      
      // Close dialog after a short delay
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Error assigning ticket:', error);
      
      // Extract the actual error message from the response if available
      let errorMessage = 'Failed to assign ticket. Please try again.';
      
      // Log the complete error object for debugging
      console.log('Complete error object:', JSON.stringify(error, Object.getOwnPropertyNames(error)));
      
      // Check all possible locations for the error message
      if (error.response) {
        console.log('Error response:', error.response);
        if (error.response.data) {
          console.log('Error response data:', error.response.data);
          
          // Check various formats the backend might use for error messages
          if (typeof error.response.data === 'string') {
            // Plain string error
            errorMessage = error.response.data;
          }
          else if (error.response.data.message) {
            // Standard message format
            errorMessage = error.response.data.message;
          } 
          else if (error.response.data.error) {
            // Error field format
            errorMessage = error.response.data.error;
          }
          else if (error.response.data.errors && error.response.data.errors.length > 0) {
            // Array of errors format
            errorMessage = error.response.data.errors[0].message || error.response.data.errors[0];
          }
          // Look for fields with the word 'message' in them
          else {
            for (const key in error.response.data) {
              if (key.toLowerCase().includes('message') && error.response.data[key]) {
                errorMessage = error.response.data[key];
                break;
              }
            }
          }
        } else if (error.response.statusText) {
          // Use status text if no data
          errorMessage = error.response.statusText;
        }
      }
      
      // Check error.message as a fallback
      if (errorMessage === 'Failed to assign ticket. Please try again.' && error.message) {
        if (error.message.includes('workflow ticket')) {
          errorMessage = error.message;
        } else if (!error.message.includes('Request failed') && !error.message.includes('Network Error')) {
          // Use error.message if it's not a generic axios error
          errorMessage = error.message;
        }
      }
      
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle assignment submission
  const handleAssign = async () => {
    if (!selectedUser) {
      setError('Please select a user');
      return;
    }

    // Check if service is not mapped (prevent assignment)
    if (mappingStatus === 'not_mapped') {
      setError('Service is not mapped to this partner. Please create the mapping first.');
      return;
    }

    if (!isOrganizationTicket() && mappingStatus === 'mapped') {
      const ticketCharge = getTicketChargeIncTax();
      const partnerCharge = getPartnerChargeIncTax();
      if (ticketCharge > 0 && partnerCharge > 0) {
        const diff = partnerCharge - ticketCharge;
        if (diff > 0) {
          setAssignConfirmMessage(
            `Assigning this user will cause AED ${diff.toFixed(2)} amount of loss for Thaklis. Do you want to proceed?`
          );
          setPendingAssign(true);
          setShowAssignConfirmDialog(true);
          return;
        }
        if (diff === 0) {
          setAssignConfirmMessage(
            'Assigning this user will not get any profit and partner receives full ticket amount. Do you want to proceed?'
          );
          setPendingAssign(true);
          setShowAssignConfirmDialog(true);
          return;
        }
      }
    }

    await performAssignApiCall();
  };

  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Assign Ticket #{ticket?.ticketNumber || ''}
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}
        
        <Box sx={{ mt: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            Ticket Subject: {ticket?.subject || 'N/A'}
          </Typography>
          
          <Divider sx={{ my: 1 }} />
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <FormControl fullWidth>
                <InputLabel id="user-select-label">Assign To User</InputLabel>
                <Select
                  labelId="user-select-label"
                  id="user-select"
                  value={selectedUser}
                  onChange={handleUserChange}
                  label="Assign To User"
                  disabled={submitting || users.length === 0}
                >
                  {users.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {/* Display username first with different formats based on available properties */}
                      {user.name ? user.name :
                       (user.firstName && user.lastName) ? `${user.firstName} ${user.lastName}` :
                       user.username ? user.username :
                       user.email}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Service Mapping Status Indicator */}
              {mappingStatus === 'checking' && (
                <Alert severity="info" icon={<CircularProgress size={20} />}>
                  Checking service mapping...
                </Alert>
              )}

              {mappingStatus === 'mapped' && (
                <Alert severity="success">
                  <Typography variant="body2">Service is mapped to this partner</Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 0.5,
                      fontWeight: 600,
                      color: (() => {
                        if (isOrganizationTicket()) return 'text.secondary';
                        const ticketCharge = getTicketChargeIncTax();
                        const partnerCharge = getPartnerChargeIncTax();
                        if (!(ticketCharge > 0 && partnerCharge > 0)) return 'text.secondary';
                        const profit = ticketCharge - partnerCharge;
                        return profit >= 0 ? 'success.main' : 'error.main';
                      })(),
                    }}
                  >
                    Thaklis Profit: AED {(() => {
                      if (isOrganizationTicket()) return '-';
                      const ticketCharge = getTicketChargeIncTax();
                      const partnerCharge = getPartnerChargeIncTax();
                      if (!(ticketCharge > 0 && partnerCharge > 0)) return '-';
                      const profit = ticketCharge - partnerCharge;
                      return profit.toFixed(2);
                    })()}
                  </Typography>
                </Alert>
              )}

              {mappingStatus === 'not_mapped' && (
                <Alert severity="warning" action={
                  <Button color="inherit" size="small" onClick={() => setShowMappingDialog(true)}>
                    MAP NOW
                  </Button>
                }>
                  <Typography variant="body2">
                    <strong>Service not mapped to this partner.</strong> You must create a service mapping before assigning this ticket.
                  </Typography>
                </Alert>
              )}

              {mappingCheckError && (
                <Alert severity="error">
                  {mappingCheckError}
                </Alert>
              )}
              
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Due Date"
                  value={dueDate}
                  onChange={handleDueDateChange}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                  disabled={submitting}
                  minDate={new Date()}
                />
              </LocalizationProvider>
              
              <TextField
                label="Assignment Notes"
                multiline
                rows={3}
                value={notes}
                onChange={handleNotesChange}
                fullWidth
                disabled={submitting}
                placeholder="Add any notes about this assignment"
              />
            </Box>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button 
          onClick={handleAssign} 
          variant="contained" 
          color="primary" 
          disabled={submitting || loading || !selectedUser || mappingStatus === 'not_mapped' || mappingStatus === 'checking'}
        >
          {submitting ? <CircularProgress size={24} /> : 'Assign Ticket'}
        </Button>
      </DialogActions>
    </Dialog>

    <Dialog
      open={showAssignConfirmDialog}
      onClose={() => {
        setShowAssignConfirmDialog(false);
        setPendingAssign(false);
      }}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>Confirm Assignment</DialogTitle>
      <DialogContent>
        <Typography variant="body2">{assignConfirmMessage}</Typography>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            setShowAssignConfirmDialog(false);
            setPendingAssign(false);
          }}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            setShowAssignConfirmDialog(false);
            if (!pendingAssign) return;
            setPendingAssign(false);
            performAssignApiCall();
          }}
          disabled={submitting}
        >
          Proceed
        </Button>
      </DialogActions>
    </Dialog>

      {/* Service Mapping Dialog - shown when service is not mapped to selected partner */}
      <ServiceMappingDialog
        open={showMappingDialog}
        onClose={() => {
          setShowMappingDialog(false);
          setSubmitting(false);
        }}
        service={ticket?.service || { id: ticket?.serviceId, serviceName: ticket?.serviceName || 'N/A' }}
        partner={selectedPartner}
        govtCharge={customerGovtCharge}
        onMappingCreated={handleMappingCreated}
      />
    </>
  );
};

export default QuickAssignDialog;
